import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteSession } from "@/hooks/api/useSessions"
import type { DeleteSessionProps } from "@/types/session"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteSession = memo(
  ({ session, onSuccess, onCancel }: DeleteSessionProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteSessionMutation = useDeleteSession()

    const displayLabel = session.name ?? session.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteSessionMutation.mutateAsync(session.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting session:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete session: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete Session"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This session will be permanently
                removed.
              </Text>
            </>
          }
          confirmText="Delete Session"
          confirmColorScheme="red"
          isLoading={deleteSessionMutation.isPending}
        />
      </>
    )
  },
)

DeleteSession.displayName = "DeleteSession"

export default DeleteSession
export type { DeleteSessionProps }
